import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { getReputation } from '../api/integrations.js';
import { useCurrentUser } from '../auth/session-context.js';
import { formatRelative } from '../lib/time.js';
import { Card } from '../ui/Card.js';

/**
 * What CrowdSec's CTI says of an address (P11.9.6, decision C9), on its page:
 * advice beside the verdict, never a reason to ban by itself (rule 11). Asked once
 * when the page opens and kept a week by the API, so the quota goes on addresses
 * somebody looked at.
 */

const REPUTATION_BADGE: Record<string, string> = {
  malicious: 'badge-danger',
  suspicious: 'badge-warning',
  known: 'badge-accent',
  safe: 'badge-success',
};

export function ReputationCard({ ip }: { ip: string }): ReactNode {
  const isAdmin = useCurrentUser().user.role === 'admin';
  const lookup = useQuery({
    queryKey: ['reputation', ip],
    queryFn: () => getReputation(ip),
    staleTime: 60 * 60_000,
    retry: false,
  });
  const data = lookup.data;
  if (data === undefined || (data.state === 'not_configured' && !isAdmin)) return null;
  const reputation = data.reputation;

  return (
    <Card
      title="Reputation (CrowdSec CTI)"
      description="What CrowdSec's network has seen of this address. Advice only; nothing here bans it."
      aside={
        reputation !== null ? (
          <span className={`badge ${REPUTATION_BADGE[reputation.reputation] ?? 'badge-muted'}`}>
            {reputation.reputation}
          </span>
        ) : undefined
      }
    >
      {data.state === 'not_configured' && (
        <p className="text-sm muted">
          Add a CrowdSec CTI key in <Link to="/integrations">Integrations</Link> to see what
          CrowdSec's network knows of an address.
        </p>
      )}
      {data.state === 'unknown' && (
        <p className="text-sm muted">CrowdSec has not seen this address attack anyone.</p>
      )}
      {(data.state === 'quota' || data.state === 'error') && (
        <p className="text-sm muted">{data.detail}</p>
      )}
      {reputation !== null && (
        <dl className="details">
          {reputation.classifications.length > 0 && (
            <>
              <dt>Known as</dt>
              <dd>{reputation.classifications.map((label) => label.label).join(', ')}</dd>
            </>
          )}
          {reputation.falsePositives.length > 0 && (
            <>
              <dt>Possibly harmless</dt>
              <dd>{reputation.falsePositives.map((label) => label.label).join(', ')}</dd>
            </>
          )}
          {reputation.behaviors.length > 0 && (
            <>
              <dt>Behaviour</dt>
              <dd>{reputation.behaviors.map((label) => label.label).join(', ')}</dd>
            </>
          )}
          {reputation.score !== null && (
            <>
              <dt>Threat score</dt>
              <dd>{reputation.score} of 5</dd>
            </>
          )}
          {(reputation.asName !== null || reputation.country !== null) && (
            <>
              <dt>Network</dt>
              <dd>
                {[reputation.asName, reputation.country]
                  .filter((part) => part !== null)
                  .join(' · ')}
              </dd>
            </>
          )}
          {reputation.lastSeen !== null && (
            <>
              <dt>Last seen attacking</dt>
              <dd>{formatRelative(reputation.lastSeen)}</dd>
            </>
          )}
        </dl>
      )}
      {data.fetchedAt !== null && (
        <p className="text-sm muted">Asked {formatRelative(data.fetchedAt)}.</p>
      )}
    </Card>
  );
}
